
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';
import { VuetifyMixin } from '@/mixins/VuetifyMixin';
import { RecurrenceRuleModel } from '@/models/calendar/RecurrenceRuleModel';
import WeekdaySelect from '@/components/calendar/WeekdaySelect.vue';
import { RecurrenceRuleFrequency } from '@/../types/enums';
import { RecurrenceRuleWeekday } from '@/../types/enums/recurrence-rule-weekday';

type RRuleItem = {
	text: string;
	value: RecurrenceRuleModel | null;
	openDialog?: boolean;
}

/** Which "Ends" option is selected in the custom recurrence dialog. */
enum RecurrenceEndOption {
	Never = 0,
	On = 1,
	After = 2,
}

type RecurrenceForm = {
	interval: number | null;
	frequency: RecurrenceRuleFrequency;
	weekdays: RecurrenceRuleWeekday[];
	count: number | null;
	endDate: Date | null;
	endOptions: RecurrenceEndOption;
}

@Component({
	components:{
		WeekdaySelect,
	}
})
export default class RecurrenceSelector extends Mixins(VuetifyMixin){
	@Prop({ default: () => new Date() }) startDate: Date;
	@Prop({ default: () => new Date() }) focusDate: Date;
	@Prop({ default: false, type: Boolean }) editMode: Date;
	@Prop({ default: null }) rule: RecurrenceRuleModel | null;

	get EndOption(): typeof RecurrenceEndOption{
		return RecurrenceEndOption;
	}

	/** The last rule we emitted, so we can tell our own updates apart from external ones. */
	private lastEmittedRule: RecurrenceRuleModel | null = null;
	private emitRule(rule: RecurrenceRuleModel | null): void{
		this.lastEmittedRule = rule;
		this.$emit('update:rule', rule);
	}
	updateRule(): void{
		this.emitRule(this.ComputedRule);
	}

	@Watch('rule', { immediate: true }) ruleChanged(rule: RecurrenceRuleModel | null): void{
		this.selectedRule = rule;
		// Ignore the echo of a rule we just emitted, otherwise typing in the dialog fights itself
		if(rule === this.lastEmittedRule) return;
		this.loadFormValueFromRule(rule);
	}

	/** Populate the custom recurrence form from an externally provided rule (ex; editing an event) */
	private loadFormValueFromRule(rule: RecurrenceRuleModel | null): void{
		if(rule === null) return;
		let endOptions = RecurrenceEndOption.Never;
		if(rule.count !== null){
			endOptions = RecurrenceEndOption.After;
		}else if(rule.until !== null){
			endOptions = RecurrenceEndOption.On;
		}
		this.formValue = {
			interval: rule.interval,
			frequency: rule.frequency,
			weekdays: rule.weekdays === null ? [] : rule.weekdays,
			count: rule.count,
			endDate: endOptions === RecurrenceEndOption.On ? rule.until : null,
			endOptions,
		};
		// A rule with both an interval and weekdays isn't one of the suggested presets, offer it as the custom option
		if(rule.interval !== null && rule.weekdays !== null && rule.weekdays.length > 0){
			this.customRule = rule;
		}
	}

	selectedRule: RecurrenceRuleModel | null = null;
	selectRule(item: RRuleItem): void {
		if(item === null) return;
		if(item === undefined) return;
		if(item.openDialog){
			this.dialog = true;
		}
		this.selectedRule = item.value;
		this.emitRule(this.selectedRule);
	}
	dialog: boolean = false;

	async datePicked(): Promise<void> {
		this.formValue.endOptions = RecurrenceEndOption.On;
		this.updateRule();
	}

	/** Presets offered in the dropdown, plus the custom rule once one has been applied */
	get Rules(): RecurrenceRuleModel[]{
		const rules = [
			RecurrenceRuleModel.WeeklyToday(this.startDate),
			RecurrenceRuleModel.MonthlyToday(this.startDate),
		];
		if(this.customRule !== null){
			rules.push(this.customRule);
		}
		return rules;
	}

	get SuggestedRules(): RRuleItem[]{
		return [
			{
				text: "Does not repeat",
				value: null,
			},
			...this.Rules.map(rule => ({
				text: rule.toText(),
				value: rule,
			})),
			{
				text: "Custom...",
				value: this.ComputedRule,
				openDialog: true,
			},
		];
	}

	get FreqItems(): { text: string, value: RecurrenceRuleFrequency }[]{
		return [
			{
				text: "Day",
				value: RecurrenceRuleFrequency.Daily,
			},
			{
				text: "Week",
				value: RecurrenceRuleFrequency.Weekly,
			},
			{
				text: "Month",
				value: RecurrenceRuleFrequency.Monthly,
			},
			{
				text: "Year",
				value: RecurrenceRuleFrequency.Yearly,
			},
		]
	}
	applyCustomRule(): void{
		this.customRule = this.ComputedRule;
		this.selectedRule = this.customRule;
		this.dialog = false;
		this.emitRule(this.customRule);
	}

	/** The Done button is only available once the dialog describes a valid rule */
	get CanApply(): boolean{
		if(this.Interval === null) return false;
		if(this.formValue.endOptions === RecurrenceEndOption.On && !this.formValue.endDate) return false;
		if(this.formValue.endOptions === RecurrenceEndOption.After && this.Count === null) return false;
		return true;
	}

	formValue: RecurrenceForm = {
		interval: 1,
		frequency: RecurrenceRuleFrequency.Weekly,
		weekdays: [],
		count: null,
		endDate: null,
		endOptions: RecurrenceEndOption.Never,
	};

	/** Text fields hand back strings, normalize them before they reach the rule */
	private get Interval(): number | null{
		const interval = Number(this.formValue.interval);
		if(!Number.isFinite(interval) || interval < 1) return null;
		return interval;
	}
	private get Count(): number | null{
		const count = Number(this.formValue.count);
		if(!Number.isFinite(count) || count < 1) return null;
		return count;
	}

	customRule: RecurrenceRuleModel | null = null;
	get ComputedRule(): RecurrenceRuleModel{
		const rule = new RecurrenceRuleModel(this.formValue.frequency);
		rule.interval = this.Interval;
		rule.weekdays = this.formValue.weekdays;
		rule.until = null;
		rule.startDate = this.editMode ? this.startDate : this.focusDate;
		if(this.formValue.endOptions === RecurrenceEndOption.On){
			rule.until = this.formValue.endDate;
		}else if (this.formValue.endOptions === RecurrenceEndOption.After){
			rule.count = this.Count;
		}
		return rule;
	}
	get ComputedRuleText(): string{
		return this.ComputedRule.toText();
	}
}
